import type { SupabaseClient } from "@supabase/supabase-js";

// Single source of truth for username rules, mirroring the zod schema on the
// /auth page (src/pages/Auth.tsx) so both signup paths validate identically:
//   • 3–30 characters
//   • letters, numbers, dots, and underscores only
//
// Usernames are stored lowercase and uniqueness is enforced case-insensitively
// at the DB level (unique index on lower(username)). Always persist the
// lowercased form via normalizeUsername().

export const USERNAME_MIN_LENGTH = 3;
export const USERNAME_MAX_LENGTH = 30;
export const USERNAME_REGEX = /^[a-zA-Z0-9._]+$/;

/** Human-readable summary shown under the username field. */
export const USERNAME_REQUIREMENTS_TEXT =
  "3–30 characters. Letters, numbers, dots, and underscores only.";

/** Lowercased, trimmed form that gets written to the database. */
export function normalizeUsername(username: string): string {
  return username.trim().toLowerCase();
}

/**
 * Returns the first unmet requirement as a user-facing message, or null if the
 * username satisfies the client-side format policy. Does NOT check availability.
 */
export function getUsernameError(username: string): string | null {
  const trimmed = username.trim();
  if (trimmed.length < USERNAME_MIN_LENGTH) {
    return `Username must be at least ${USERNAME_MIN_LENGTH} characters`;
  }
  if (trimmed.length > USERNAME_MAX_LENGTH) {
    return `Username must be less than ${USERNAME_MAX_LENGTH} characters`;
  }
  if (!USERNAME_REGEX.test(trimmed)) {
    return "Only letters, numbers, dots, and underscores";
  }
  return null;
}

/**
 * Soft availability check against the profiles table. This is a UX nicety only;
 * the authoritative guarantee is the case-insensitive unique index on
 * lower(username), which rejects a duplicate even if two clients race past this
 * check. Returns true when the username appears free.
 */
export async function isUsernameAvailable(
  supabase: SupabaseClient,
  username: string,
): Promise<boolean> {
  const { data } = await supabase
    .from("profiles")
    .select("id")
    .eq("username", normalizeUsername(username))
    .maybeSingle();
  return data === null;
}

/**
 * Strip a name down to a valid username "base": lowercase, only the allowed
 * characters, capped so there's room to append digits if needed. Returns "" if
 * nothing usable remains.
 */
export function deriveUsernameBase(first: string, last: string): string {
  const raw = `${first ?? ""}${last ?? ""}`
    .toLowerCase()
    .replace(/[^a-z0-9._]/g, "");
  // Leave headroom (up to 4 digits) under the max so suffixed variants still fit.
  return raw.slice(0, USERNAME_MAX_LENGTH - 4);
}

/**
 * Suggest an available username derived from the user's name (Instagram/X
 * style: pre-fill a handle they can accept with one tap). Runs a single prefix
 * query for everything already taken under the base, then returns the first
 * free candidate — the bare base, then base2, base3, … — so the caller never
 * has to show the user a "that's taken" rejection on a name they didn't pick.
 *
 * The returned value is guaranteed to satisfy getUsernameError() (>= min
 * length, allowed chars). Availability is best-effort; the unique index is the
 * real guarantee, and the upsert path already handles a lost race (23505).
 */
export async function suggestUsername(
  supabase: SupabaseClient,
  first: string,
  last: string,
): Promise<string> {
  let base = deriveUsernameBase(first, last);
  // Pad pathologically short bases (single-letter names) so even the bare
  // candidate clears the minimum length.
  if (base.length > 0 && base.length < USERNAME_MIN_LENGTH) {
    base = base.padEnd(USERNAME_MIN_LENGTH, "0");
  }
  if (!base) base = "coffee"; // no usable name characters at all

  // One round-trip: pull everything already taken under this base prefix.
  const { data } = await supabase
    .from("profiles")
    .select("username")
    .ilike("username", `${base}%`)
    .limit(500);
  const taken = new Set(
    (data ?? []).map((r) => normalizeUsername((r as { username: string | null }).username ?? "")),
  );

  if (base.length >= USERNAME_MIN_LENGTH && !taken.has(base)) return base;
  for (let i = 2; i < 10000; i++) {
    const candidate = `${base}${i}`.slice(0, USERNAME_MAX_LENGTH);
    if (!taken.has(candidate)) return candidate;
  }
  // Extremely unlikely fallback.
  return `${base}${Math.floor(Math.random() * 100000)}`.slice(0, USERNAME_MAX_LENGTH);
}

// Deterministic, friendly, policy-valid handle derived from a user id. Used as
// a *display-time* fallback for users who were silently stamped with a
// non-distinguishing name (e.g. legacy "Grounded Member" Apple users) and as a
// skip-time persisted handle so they never render as "Grounded M." again.
// Collisions are possible but rare and acceptable for this purpose; the unique
// index on lower(username) is still the authoritative guarantee on writes.
const FALLBACK_ADJECTIVES = [
  "cozy", "warm", "quiet", "sunny", "brewed", "mellow", "calm", "bright",
  "steamy", "roasted", "frothy", "smooth", "rich", "bold", "snug", "happy",
  "drowsy", "kind", "swift", "gentle", "nimble", "merry", "wander", "curious",
];
const FALLBACK_NOUNS = [
  "bean", "mug", "latte", "roast", "cup", "pour", "brew", "drip",
  "kettle", "press", "crema", "espresso", "mocha", "foam", "grind", "filter",
  "barista", "sip", "blend", "carafe", "thermos", "saucer", "tumbler", "cafe",
];

export function fallbackHandleFromId(userId: string): string {
  if (!userId) return "coffee000";
  // FNV-1a 32-bit hash — deterministic, tiny, no deps.
  let h = 0x811c9dc5;
  for (let i = 0; i < userId.length; i++) {
    h ^= userId.charCodeAt(i);
    h = Math.imul(h, 0x01000193);
  }
  h = h >>> 0;
  const adj = FALLBACK_ADJECTIVES[h % FALLBACK_ADJECTIVES.length];
  const noun = FALLBACK_NOUNS[(h >>> 8) % FALLBACK_NOUNS.length];
  const suffix = String((h >>> 16) % 1000).padStart(3, "0");
  return `${adj}${noun}${suffix}`;
}

